import { useClientID } from "@cs124/client-id";
import {
  FullResponse,
  semesterFromShorthand
} from "@cs124/person";
import keyBy from "lodash/keyBy";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
export const PersonableProvider = ({
  server,
  semester,
  loggedIn,
  shouldConnect,
  children,
  ...props
}) => {
  const [course, setCourse] = useState(props.course);
  const [loaded, setLoaded] = useState(false);
  const { browserID, tabID } = useClientID();
  const [impersonating, setImpersonating] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("personable:impersonating") || void 0
  );
  const headers = useMemo(() => {
    return {
      "Content-Type": "application/json",
      headerVersion: "2025.1.0",
      number: "124",
      semester,
      browserID,
      tabID,
      ...impersonating && { impersonating }
    };
  }, [semester, browserID, tabID, impersonating]);
  const update = useCallback(async () => {
    try {
      const course2 = await fetch(`${server}/course/?staff=true`, {
        headers,
        credentials: "include"
      }).then((r) => r.json());
      setCourse(FullResponse.check(course2));
      return course2;
    } finally {
      setLoaded(true);
    }
  }, [server, headers]);
  useEffect(() => {
    if (shouldConnect) {
      update();
    }
  }, [update, shouldConnect]);
  const impersonate = useCallback(
    (netID) => {
      if (netID === impersonating) {
        console.warn(`Impersonating state already correct`);
        return;
      }
      netID ? sessionStorage.setItem("personable:impersonating", netID) : sessionStorage.removeItem("personable:impersonating");
      setImpersonating(netID);
    },
    [impersonating]
  );
  const addStudent = useCallback(
    async (netID) => {
      if (!loggedIn) {
        return;
      }
      const response = await fetch(`${server}/student/${netID}`, {
        method: "POST",
        headers,
        credentials: "include"
      });
      response.status === 200 && update();
      return response.status === 200;
    },
    [loggedIn, server, headers, update]
  );
  const addStaff = useCallback(async () => {
    if (!loggedIn) {
      return;
    }
    const response = await fetch(`${server}/staff/`, {
      method: "POST",
      headers,
      credentials: "include"
    });
    response.status === 200 && update();
    return response.status === 200;
  }, [server, headers, loggedIn, update]);
  const changeRole = useCallback(
    async (netID, role) => {
      if (!loggedIn) {
        return;
      }
      const response = await fetch(`${server}/staff/${netID}/${role}`, {
        method: "PUT",
        headers,
        credentials: "include"
      });
      response.status === 200 && update();
      return response.status === 200;
    },
    [loggedIn, server, headers, update]
  );
  const removeStaff = useCallback(
    async (netID) => {
      if (!loggedIn) {
        return;
      }
      const response = await fetch(`${server}/staff/${netID}`, {
        method: "DELETE",
        headers,
        credentials: "include"
      });
      response.status === 200 && update();
      return response.status === 200;
    },
    [server, headers, update, loggedIn]
  );
  const updateProfile = useCallback(
    async (profile) => {
      if (!loggedIn) {
        return;
      }
      const response = await fetch(`${server}/profile/`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(profile)
      });
      response.status === 200 && update();
      return response.status === 200;
    },
    [server, headers, update, loggedIn]
  );
  const getProfile = useCallback(
    async (netID) => {
      if (!loggedIn) {
        return;
      }
      try {
        return await fetch(`${server}/profile/${netID}`, { headers, credentials: "include" }).then((r) => r.json());
      } catch (err) {
        return;
      }
    },
    [server, headers, loggedIn]
  );
  const updateLanguage = useCallback(
    async (language) => {
      if (!loggedIn) {
        return;
      }
      const response = await fetch(`${server}/language/`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ language })
      });
      response.status === 200 && update();
      return response.status === 200;
    },
    [server, headers, update, loggedIn]
  );
  const staffByEmail = course?.staff ? keyBy(course.staff, "email") : {};
  return /* @__PURE__ */ React.createElement(
    PersonableContext.Provider,
    {
      value: {
        available: true,
        loaded,
        course,
        reload: update,
        impersonate,
        impersonating,
        addStaff,
        addStudent,
        server,
        changeRole,
        removeStaff,
        updateProfile,
        getProfile,
        updateLanguage,
        headers,
        currentSemester: semesterFromShorthand(semester),
        staffByEmail
      }
    },
    children
  );
};
export const PersonableContext = createContext({
  available: false,
  loaded: false,
  reload: () => {
    throw new Error("Not configured");
  },
  impersonate: () => {
    throw new Error("Not configured");
  },
  server: "",
  headers: {},
  currentSemester: void 0,
  staffByEmail: {}
});
export const usePersonable = () => useContext(PersonableContext);
//# sourceMappingURL=index.js.map
